import React from 'react';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { StarIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import Button from '@/ui/components/shared/Button';
import { useTranslations } from 'next-intl';
import { Accordion } from '@/ui/components/shared/accordion/accordion';
import { ImageFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import CartAddIcon from '../../../../../../public/images/cart-add.svg';

export type RecommendedProductProps = {
  name: string;
  image?: ImageFragment | null;
  price?: number;
  discountedPrice?: number;
  path: string;
  topChoice?: string;
  reasons?: string[];
  productFeatures?: RecommendedProductFeature[];
  onSelect: (path: string, name: string, isTopChoice: boolean) => void; // used for add to cart
  onExtendFeatures?: (isTopChoice: boolean) => void;
  onExtendReasons?: (isTopChoice: boolean) => void;
};

export type RecommendedProductFeature = {
  text: string;
  imageUrl: string;
};

function RecommendedProduct({
  name,
  image,
  price,
  discountedPrice,
  path,
  topChoice,
  reasons,
  productFeatures,
  onSelect,
  onExtendReasons,
  onExtendFeatures,
}: RecommendedProductProps) {
  const t = useTranslations('RecommendedProduct');
  const { formatCurrency } = useCurrencyFormatter();

  const hasReasons = reasons && reasons.length > 0;
  const hasProductFeatures = productFeatures && productFeatures.length > 0;
  const isTopChoice = !!topChoice;

  return (
    <div className="flex w-full flex-col">
      <div onClick={() => onSelect(path, name, !!topChoice)} className="mb-3">
        <div className="relative flex w-full justify-center rounded-t-xl border border-b-0 border-neutral-100 bg-white p-4">
          {topChoice && (
            <div className="absolute left-3 top-3 flex items-center space-x-1 rounded-sm bg-neutral-100 px-2 py-1 text-white">
              <StarIcon className="h-5 w-5 text-primary-100" />
              <span className="text-sm font-semibold">{topChoice}</span>
            </div>
          )}
          <OptimizedImage
            image={image}
            sizes="(max-width: 768px) 90vw, 20vw"
            width={250}
            height={250}
          />
        </div>
        <Button
          variant="yellowBlackBorder"
          onClick={() => onSelect(path, name, isTopChoice)}
          className="w-full rounded-t-none"
        >
          <Image src={CartAddIcon} alt="Add to cart" width={24} height={24} className="mr-2" />
          {t('addToCart')}
        </Button>
      </div>

      <div className="mb-1 flex justify-between gap-4">
        <button type="button" onClick={() => onSelect(path, name, isTopChoice)}>
          <h3 className="text-wrap text-start text-md font-bold">{name}</h3>
        </button>
        <div className="flex-wrap">
          {discountedPrice && (
            <p className="mr-2 text-md text-neutral-40 line-through">
              {formatCurrency(discountedPrice)}
            </p>
          )}
          <p className="text-md font-semibold">{formatCurrency(price)}</p>
        </div>
      </div>

      {topChoice && hasReasons && (
        <Accordion
          open={isTopChoice}
          title={t('topChoiceReasonsTitle')}
          onOpened={() => {
            if (onExtendReasons) {
              onExtendReasons(isTopChoice);
            }
          }}
        >
          <ul
            className="list-inside"
            style={{ listStyleImage: 'url(/images/checkmark_black_yellow.svg)' }}
          >
            {reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </Accordion>
      )}

      {hasReasons && hasProductFeatures && <hr />}

      {hasProductFeatures && (
        <Accordion
          title={t('showProductFeaturesTitle')}
          onOpened={() => {
            if (onExtendFeatures) {
              onExtendFeatures(isTopChoice);
            }
          }}
        >
          <div className="flex justify-center">
            <ul className="grid max-w-xl grid-cols-2 gap-4 p-4">
              {productFeatures.map((feature, i) => (
                <li className="flex flex-col items-center" key={i}>
                  <img src={feature.imageUrl} alt={feature.text} width={50} />
                  <span className="block text-center">{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </Accordion>
      )}
    </div>
  );
}

export default RecommendedProduct;
